import { useCallback, useRef, useState } from 'react';
import { useAuthStore } from '../../features/auth/store';
import { getOspreyApiBaseUrl } from '../utils/apiBase';

// PE-1283 §7 E4 — osprey video uploader.
//
// Images keep the existing proxied multipart path (useMediaUpload). VIDEO uses the
// signed direct-to-GCS path so large files never flow through the 50MB proxied
// /media/upload endpoint:
//   1. POST /media/upload-url { filename, mimeType, access:'public' }
//        → { uploadUrl, method:'PUT', objectKey, bucket, requiredHeaders }
//   2. PUT the raw file straight to GCS with the requiredHeaders
//   3. POST /media/confirm { objectKey, bucket, mimeType, sizeBytes, access:'public' }
//        → { id, transcodeState }
//   4. poll GET /media/:id until transcodeState === 'done'
//        → { transcodeState, derivedWebmKey, posterKey, cdnUrl }
// The playable url is built from derivedWebmKey (public bucket) and the poster from
// posterKey; while still transcoding the caller may persist status:'PROCESSING'.

export type MediaTranscodeState = 'original' | 'processing' | 'done' | 'failed' | string | null;

export interface UploadUrlResponse {
  uploadUrl: string;
  method: 'PUT';
  objectKey: string;
  bucket: string;
  maxBytes: number;
  requiredHeaders: Record<string, string>;
  expiresAt: string;
}

export interface ConfirmResponse {
  id: string;
  transcodeState: MediaTranscodeState;
}

export interface MediaStatusResponse {
  id: string;
  transcodeState: MediaTranscodeState;
  derivedWebmKey: string | null;
  posterKey: string | null;
  cdnUrl: string | null;
}

// Public https URL for an object in a public GCS bucket — mirrors media-service
// FileStorageService.publicUrl so the derived webm/poster keys resolve directly.
export function publicGcsUrl(bucket: string, objectKey: string): string {
  return `https://storage.googleapis.com/${bucket}/${objectKey}`;
}

// Signed video upload flow goes through osprey-api (proxies /media/* to media-service).
// The Osprey UI talks ONLY to osprey-api — single origin. See apiBase.ts.
const getBaseUrl = getOspreyApiBaseUrl;

function authHeaders(solutionId: string): Record<string, string> {
  const { token, selectedTenantId } = useAuthStore.getState();
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (selectedTenantId) headers['x-tenant-id'] = selectedTenantId;
  headers['x-solution-id'] = solutionId;
  return headers;
}

async function jsonOrThrow<T>(res: Response, fallback: string): Promise<T> {
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err?.message ?? `${fallback}: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

// Poll cadence: ~2s interval, capped at ~5 minutes (150 attempts) so a stuck
// transcode surfaces FAILED instead of polling forever.
const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 150;

export interface VideoUploadResult {
  mediaId: string;
  /** Playable url — derived webm when done, else the original cdnUrl. */
  url: string;
  posterUrl?: string;
  transcodeState: MediaTranscodeState;
  /** True when the asset is still transcoding (caller persists status:'PROCESSING'). */
  processing: boolean;
}

export function useProductMedia() {
  const [error, setError] = useState<string | null>(null);
  const cancelledRef = useRef(false);

  const requestUploadUrl = useCallback(
    async (file: File, solutionId: string): Promise<UploadUrlResponse> => {
      const res = await fetch(`${getBaseUrl()}/media/upload-url`, {
        method: 'POST',
        headers: { ...authHeaders(solutionId), 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name, mimeType: file.type, access: 'public' }),
      });
      return jsonOrThrow<UploadUrlResponse>(res, 'Could not start upload');
    },
    [],
  );

  const putToGcs = useCallback(
    async (file: File, signed: UploadUrlResponse): Promise<void> => {
      const res = await fetch(signed.uploadUrl, {
        method: 'PUT',
        headers: signed.requiredHeaders,
        body: file,
      });
      if (!res.ok) {
        throw new Error(`Upload to storage failed: ${res.status}`);
      }
    },
    [],
  );

  const confirm = useCallback(
    async (file: File, signed: UploadUrlResponse, solutionId: string): Promise<ConfirmResponse> => {
      const res = await fetch(`${getBaseUrl()}/media/confirm`, {
        method: 'POST',
        headers: { ...authHeaders(solutionId), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          objectKey: signed.objectKey,
          bucket: signed.bucket,
          mimeType: file.type,
          sizeBytes: file.size,
          access: 'public',
        }),
      });
      return jsonOrThrow<ConfirmResponse>(res, 'Could not register upload');
    },
    [],
  );

  const getStatus = useCallback(
    async (id: string, solutionId: string): Promise<MediaStatusResponse> => {
      const res = await fetch(`${getBaseUrl()}/media/${id}`, {
        method: 'GET',
        headers: authHeaders(solutionId),
      });
      return jsonOrThrow<MediaStatusResponse>(res, 'Could not read media status');
    },
    [],
  );

  /**
   * Run the full signed video flow. Calls `onState` on each transition so the UI
   * can reflect PROCESSING. Resolves once the asset is `done` (webm + poster) or
   * the poll cap is hit while still processing (returns processing:true so the
   * caller can submit non-blocking with status:'PROCESSING'). Throws on `failed`.
   */
  const uploadVideo = useCallback(
    async (
      file: File,
      solutionId: string,
      onState?: (state: MediaTranscodeState) => void,
    ): Promise<VideoUploadResult> => {
      setError(null);
      cancelledRef.current = false;
      try {
        const signed = await requestUploadUrl(file, solutionId);
        await putToGcs(file, signed);
        const confirmed = await confirm(file, signed, solutionId);
        onState?.(confirmed.transcodeState);

        // Resolve the playable url for a done asset. For a TRANSCODED video the
        // webm lives at derivedWebmKey in the (public) upload bucket — prefer it.
        // For an already-webm upload derivedWebmKey is null and the original IS
        // the webm, so cdnUrl (or the original objectKey) is correct.
        const resolveDone = (status: MediaStatusResponse): VideoUploadResult => ({
          mediaId: confirmed.id,
          url: status.derivedWebmKey
            ? publicGcsUrl(signed.bucket, status.derivedWebmKey)
            : (status.cdnUrl ?? publicGcsUrl(signed.bucket, signed.objectKey)),
          posterUrl: status.posterKey ? publicGcsUrl(signed.bucket, status.posterKey) : undefined,
          transcodeState: 'done',
          processing: false,
        });

        // Already-ready (e.g. an uploaded .webm) → done immediately.
        if (confirmed.transcodeState === 'done') {
          const status = await getStatus(confirmed.id, solutionId);
          return resolveDone(status);
        }

        // Poll until done | failed | cap.
        for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
          if (cancelledRef.current) {
            throw new Error('Upload cancelled');
          }
          await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
          const status = await getStatus(confirmed.id, solutionId);
          onState?.(status.transcodeState);

          if (status.transcodeState === 'done') {
            return resolveDone(status);
          }
          if (status.transcodeState === 'failed') {
            throw new Error('Video transcoding failed');
          }
        }

        // Still processing after the cap — return non-blocking so the merchant can
        // submit; the storefront/poll resolves the final webm later.
        return {
          mediaId: confirmed.id,
          url: publicGcsUrl(signed.bucket, signed.objectKey),
          posterUrl: undefined,
          transcodeState: 'processing',
          processing: true,
        };
      } catch (e: unknown) {
        const msg = (e as Error).message ?? 'Video upload failed';
        setError(msg);
        throw e;
      }
    },
    [requestUploadUrl, putToGcs, confirm, getStatus],
  );

  const cancel = useCallback(() => {
    cancelledRef.current = true;
  }, []);

  return { uploadVideo, cancel, error };
}
